import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 经济金融 - 横屏「财经仪表盘」风格
 *
 * 核心亮点：
 * - K 线图（蜡烛图）逐根绘制
 * - 关键指标数字滚动动画
 * - 仪表盘环形进度条
 * - 资金流向箭头动画
 * - Bloomberg/Reuters 财经终端风格
 *
 * 尺寸：1920x1080 横屏
 */

interface CandleStick {
  open: number;
  close: number;
  high: number;
  low: number;
  label: string;
}

interface MetricCard {
  label: string;
  value: number;
  suffix: string;
  change: number;
  color: string;
}

interface FinanceDashboardTemplateProps {
  topic: string;
  subtitle: string;
  candles: CandleStick[];
  metrics: MetricCard[];
  keyInsight: string;
  riskLevel: 'low' | 'medium' | 'high';
  tips: string[];
}

const DEFAULT_PROPS: FinanceDashboardTemplateProps = {
  topic: 'A 股市场核心指标解读',
  subtitle: 'Market Dashboard · Real-time Analysis',
  candles: [
    { open: 3100, close: 3150, high: 3180, low: 3080, label: 'Mon' },
    { open: 3150, close: 3120, high: 3170, low: 3100, label: 'Tue' },
    { open: 3120, close: 3200, high: 3220, low: 3110, label: 'Wed' },
    { open: 3200, close: 3180, high: 3230, low: 3160, label: 'Thu' },
    { open: 3180, close: 3260, high: 3280, low: 3170, label: 'Fri' },
    { open: 3260, close: 3310, high: 3330, low: 3240, label: 'Mon' },
    { open: 3310, close: 3280, high: 3340, low: 3260, label: 'Tue' },
    { open: 3280, close: 3350, high: 3370, low: 3270, label: 'Wed' },
  ],
  metrics: [
    { label: '上证指数', value: 3350, suffix: '', change: 3.2, color: '#ef4444' },
    { label: '成交量', value: 8500, suffix: '亿', change: 12.5, color: '#fbbf24' },
    { label: 'PE 估值', value: 14.2, suffix: 'x', change: -1.8, color: '#22c55e' },
    { label: '北向资金', value: 156, suffix: '亿', change: 8.3, color: '#818cf8' },
  ],
  keyInsight: '量价齐升 + 北向持续流入 = 市场情绪回暖信号，但需警惕短期获利回吐',
  riskLevel: 'medium',
  tips: [
    '关注成交量是否能持续维持在 8000 亿以上',
    'PE 估值仍低于历史均值，安全边际充足',
    '北向资金连续 5 日净流入，外资态度积极',
  ],
};

const AnimCounter: React.FC<{ target: number; frame: number; start: number; decimals?: number }> = ({
  target, frame, start, decimals = 0,
}) => {
  const p = interpolate(frame, [start, start + 40], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const eased = 1 - Math.pow(1 - p, 3);
  const val = target * eased;
  return <>{decimals > 0 ? val.toFixed(decimals) : Math.floor(val).toLocaleString()}</>;
};

export const FinanceDashboardTemplate: React.FC<Partial<FinanceDashboardTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const PAD = 60;

  const chartX = PAD;
  const chartY = 200;
  const chartW = 1040;
  const chartH = 430;

  const allPrices = p.candles.flatMap((c) => [c.high, c.low]);
  const maxPrice = Math.max(...allPrices);
  const minPrice = Math.min(...allPrices);
  const priceRange = maxPrice - minPrice;

  const priceToY = (price: number) => chartY + chartH - ((price - minPrice) / priceRange) * chartH;
  const candleW = Math.min(52, (chartW - 60) / p.candles.length - 14);

  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });
  const chartDraw = interpolate(frame, [30, 30 + p.candles.length * 12], [0, p.candles.length], { extrapolateRight: 'clamp' });

  const metricsStart = 50;
  const insightStart = metricsStart + p.metrics.length * 25 + 40;
  const insightOpacity = interpolate(frame, [insightStart, insightStart + 25], [0, 1], { extrapolateRight: 'clamp' });
  const tipsStart = insightStart + 40;

  const riskMap = {
    low: { angle: 40, color: '#22c55e', text: '低风险' },
    medium: { angle: 90, color: '#fbbf24', text: '中风险' },
    high: { angle: 140, color: '#ef4444', text: '高风险' },
  };
  const riskInfo = riskMap[p.riskLevel];

  const tickerOffset = (frame * 2) % 2000;

  return (
    <AbsoluteFill
      style={{
        background: '#0c0f14',
        fontFamily: '-apple-system, "PingFang SC", "SF Mono", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Grid bg */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Ticker bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 42,
          background: 'rgba(255,255,255,0.02)',
          borderBottom: '1px solid rgba(255,255,255,0.04)',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          fontSize: 20,
          fontFamily: '"SF Mono", monospace',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 48,
            whiteSpace: 'nowrap',
            transform: `translateX(${-tickerOffset}px)`,
          }}
        >
          {[
            'SHCOMP 3350.25 ▲+3.2%',
            'SZCOMP 11280 ▲+2.1%',
            'CSI300 4120 ▲+2.8%',
            'HSI 18500 ▼-0.5%',
            'SPX 5200 ▲+0.3%',
            'USDJPY 150.2 ▼-0.1%',
            'BTC 67500 ▲+4.2%',
            'GOLD 2380 ▲+0.8%',
          ].map((t, i) => (
            <span key={i} style={{ color: t.includes('▲') ? '#22c55e' : '#ef4444' }}>
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 58,
          left: PAD,
          right: PAD,
          opacity: headerProgress,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <div
            style={{
              padding: '5px 14px',
              background: '#ef444418',
              border: '1px solid #ef444440',
              borderRadius: 8,
              fontSize: 20,
              color: '#ef4444',
              fontWeight: 600,
            }}
          >
            📊 FINANCE
          </div>
          <div
            style={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: '#22c55e',
              boxShadow: '0 0 8px #22c55e',
            }}
          />
          <span style={{ fontSize: 20, color: '#22c55e', fontWeight: 600 }}>LIVE</span>
        </div>
        <h1 style={{ fontSize: 44, fontWeight: 800, margin: 0, letterSpacing: '-0.01em' }}>{p.topic}</h1>
        <p style={{ fontSize: 24, color: 'rgba(255,255,255,0.3)', margin: '4px 0 0' }}>{p.subtitle}</p>
      </div>

      {/* K-LINE CHART */}
      <svg
        style={{ position: 'absolute', left: chartX, top: chartY - 10 }}
        width={chartW + 20}
        height={chartH + 40}
        viewBox={`${chartX - 10} ${chartY - 10} ${chartW + 20} ${chartH + 40}`}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const y = chartY + (1 - t) * chartH;
          const price = minPrice + t * priceRange;
          return (
            <g key={t}>
              <line
                x1={chartX}
                y1={y}
                x2={chartX + chartW}
                y2={y}
                stroke="rgba(255,255,255,0.04)"
                strokeWidth="1"
              />
              <text
                x={chartX + chartW + 8}
                y={y + 4}
                fill="rgba(255,255,255,0.25)"
                fontSize="22"
                fontFamily="monospace"
              >
                {Math.round(price)}
              </text>
            </g>
          );
        })}

        {p.candles.map((candle, i) => {
          if (i >= chartDraw) return null;
          const x = chartX + 30 + i * ((chartW - 60) / p.candles.length);
          const isGreen = candle.close >= candle.open;
          const color = isGreen ? '#ef4444' : '#22c55e';

          const bodyTop = priceToY(Math.max(candle.open, candle.close));
          const bodyBottom = priceToY(Math.min(candle.open, candle.close));
          const bodyH = Math.max(bodyBottom - bodyTop, 2);

          const scaleAnim = spring({
            frame: frame - (30 + i * 12),
            fps,
            from: 0,
            to: 1,
            config: { damping: 14, stiffness: 200, mass: 0.4 },
          });

          return (
            <g key={i} opacity={scaleAnim}>
              <line
                x1={x + candleW / 2}
                y1={priceToY(candle.high)}
                x2={x + candleW / 2}
                y2={priceToY(candle.low)}
                stroke={color}
                strokeWidth="2"
              />
              <rect x={x} y={bodyTop} width={candleW} height={bodyH} fill={color} rx="2" opacity="0.85" />
              <text
                x={x + candleW / 2}
                y={chartY + chartH + 18}
                fill="rgba(255,255,255,0.3)"
                fontSize="22"
                textAnchor="middle"
              >
                {candle.label}
              </text>
            </g>
          );
        })}
      </svg>

      {/* RIGHT PANEL: Metrics */}
      <div style={{ position: 'absolute', top: 190, right: PAD, width: 680 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {p.metrics.map((m, i) => {
            const start = metricsStart + i * 25;
            const progress = spring({
              frame: frame - start,
              fps,
              from: 0,
              to: 1,
              config: { damping: 12, stiffness: 140, mass: 0.5 },
            });

            return (
              <div
                key={i}
                style={{
                  padding: 22,
                  background: `${m.color}06`,
                  borderRadius: 16,
                  border: `1px solid ${m.color}20`,
                  opacity: progress,
                  transform: `scale(${0.9 + progress * 0.1})`,
                }}
              >
                <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.4)', marginBottom: 8 }}>{m.label}</div>
                <div style={{ fontSize: 34, fontWeight: 800, color: 'white', fontVariantNumeric: 'tabular-nums' }}>
                  <AnimCounter target={m.value} frame={frame} start={start} decimals={m.value % 1 !== 0 ? 1 : 0} />
                  <span style={{ fontSize: 30, color: 'rgba(255,255,255,0.4)', marginLeft: 4 }}>{m.suffix}</span>
                </div>
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 600,
                    color: m.change >= 0 ? '#ef4444' : '#22c55e',
                    marginTop: 6,
                  }}
                >
                  {m.change >= 0 ? '▲' : '▼'} {Math.abs(m.change)}%
                </div>
              </div>
            );
          })}
        </div>

        {/* Risk gauge */}
        <div
          style={{
            marginTop: 20,
            padding: 22,
            background: 'rgba(255,255,255,0.02)',
            borderRadius: 16,
            border: '1px solid rgba(255,255,255,0.04)',
            display: 'flex',
            alignItems: 'center',
            gap: 24,
            opacity: interpolate(frame, [metricsStart + 100, metricsStart + 120], [0, 1], {
              extrapolateRight: 'clamp',
            }),
          }}
        >
          <svg width="120" height="70" viewBox="0 0 120 70">
            <path
              d="M12,65 A48,48 0 0,1 108,65"
              fill="none"
              stroke="rgba(255,255,255,0.06)"
              strokeWidth="9"
              strokeLinecap="round"
            />
            <path
              d="M12,65 A48,48 0 0,1 108,65"
              fill="none"
              stroke={riskInfo.color}
              strokeWidth="9"
              strokeLinecap="round"
              strokeDasharray="151"
              strokeDashoffset={151 - (riskInfo.angle / 180) * 151}
            />
            <line
              x1="60"
              y1="65"
              x2={60 + 36 * Math.cos(Math.PI - (riskInfo.angle / 180) * Math.PI)}
              y2={65 + 36 * Math.sin(Math.PI - (riskInfo.angle / 180) * Math.PI)}
              stroke="white"
              strokeWidth="2.5"
            />
            <circle cx="60" cy="65" r="5" fill="white" />
          </svg>
          <div>
            <div style={{ fontSize: 20, color: 'rgba(255,255,255,0.35)' }}>风险等级</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: riskInfo.color }}>{riskInfo.text}</div>
          </div>
        </div>
      </div>

      {/* BOTTOM: Insight + Tips */}
      <div
        style={{
          position: 'absolute',
          bottom: 56,
          left: PAD,
          right: PAD,
          display: 'flex',
          gap: 28,
        }}
      >
        {/* Key insight */}
        <div
          style={{
            flex: 1,
            padding: '22px 28px',
            background: 'rgba(239,68,68,0.04)',
            border: '1px solid rgba(239,68,68,0.15)',
            borderRadius: 16,
            opacity: insightOpacity,
          }}
        >
          <div
            style={{
              fontSize: 24,
              color: '#ef4444',
              fontWeight: 600,
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            📈 核心判断
          </div>
          <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>{p.keyInsight}</div>
        </div>

        {/* Tips */}
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 26,
              color: 'rgba(255,255,255,0.35)',
              marginBottom: 12,
              letterSpacing: '0.08em',
            }}
          >
            🎯 关注要点
          </div>
          {p.tips.map((tip, i) => {
            const start = tipsStart + i * 25;
            const opacity = interpolate(frame, [start, start + 20], [0, 1], { extrapolateRight: 'clamp' });
            return (
              <div
                key={i}
                style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10, opacity }}
              >
                <div
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: '#fbbf24',
                    marginTop: 10,
                    flexShrink: 0,
                    opacity: 0.6,
                  }}
                />
                <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>{tip}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #22c55e, #ef4444)',
        }}
      />
    </AbsoluteFill>
  );
};
